import Icon from "@react-native-vector-icons/material-design-icons/static";
import { memo, useEffect, useState } from "react";
import { Pressable, StyleSheet } from "react-native";
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withTiming,
	Easing,
	interpolateColor,
} from "react-native-reanimated";
import { useAppTheme } from "../../useAppTheme";

export type SwitchProps = {
	selected: boolean;
	onSelectedChange?: (selected: boolean) => void;
	disabled?: boolean;
	icon?: boolean;
};

const TRACK_WIDTH = 52;
const TRACK_HEIGHT = 32;
const KNOB_SIZE = 24;
const KNOB_BACKGROUND_SIZE = 40;
const UNSELECTED_NO_ICON_SCALE = 16 / 24;
const PRESSED_SCALE = 28 / 24;
const ANIMATION_CONFIG = {
	duration: 200,
	easing: Easing.bezier(0.4, 0.0, 0.2, 1),
};

type SwitchIconProps = {
	selected: boolean;
	color: string;
};

const SwitchIcon = memo(({ selected, color }: SwitchIconProps) => {
	if (selected) {
		return <Icon name="check" size={16} color={color} />;
	}

	return <Icon name="close" size={16} color={color} />;
});

export const Switch = memo(
	({
		selected,
		onSelectedChange,
		disabled = false,
		icon = false,
	}: SwitchProps) => {
		const theme = useAppTheme();
		const position = useSharedValue(selected ? 1 : 0);
		const [isHovered, setIsHovered] = useState(false);
		const [isFocused, setIsFocused] = useState(false);
		const [isPressed, setIsPressed] = useState(false);

		useEffect(() => {
			position.value = withTiming(selected ? 1 : 0, ANIMATION_CONFIG);
		}, [selected, position]);

		const getKnobScale = () => {
			if (isPressed) {
				return PRESSED_SCALE;
			}
			if (!(selected || icon)) {
				return UNSELECTED_NO_ICON_SCALE;
			}
			return 1;
		};

		const knobScale = getKnobScale();

		const knobAnimatedStyle = useAnimatedStyle(() => {
			const maxTranslation = TRACK_WIDTH - KNOB_SIZE - 8;

			return {
				transform: [
					{ translateX: position.value * maxTranslation },
					{ scale: knobScale },
				],
			};
		}, [position, knobScale, isPressed, selected, icon]);

		const knobBackgroundAnimatedStyle = useAnimatedStyle(() => {
			const maxTranslation = TRACK_WIDTH - KNOB_SIZE - 8;
			const opacity = isHovered || isFocused || isPressed ? 0.12 : 0;

			return {
				transform: [{ translateX: position.value * maxTranslation }],
				opacity,
				backgroundColor: selected
					? theme.colors.primary
					: theme.colors.onSurfaceVariant,
			};
		}, [position, isHovered, isFocused, isPressed, selected]);

		const trackAnimatedStyle = useAnimatedStyle(() => {
			const backgroundColor = interpolateColor(
				position.value,
				[0, 1],
				[
					disabled ? theme.colors.surfaceVariant : theme.colors.surfaceVariant,
					disabled ? theme.colors.onSurface : theme.colors.primary,
				],
			);

			return {
				backgroundColor,
				borderWidth: 2,
				borderColor: selected ? theme.colors.primary : theme.colors.outline,
				opacity: disabled ? 0.12 : 1,
			};
		}, [
			position,
			disabled,
			selected,
			theme.colors.surfaceDisabled,
			theme.colors.surfaceVariant,
			theme.colors.primary,
			theme.colors.outline,
		]);

		return (
			<Pressable
				onPress={() => !disabled && onSelectedChange?.(!selected)}
				onHoverIn={() => setIsHovered(true)}
				onHoverOut={() => setIsHovered(false)}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				onPressIn={() => setIsPressed(true)}
				onPressOut={() => setIsPressed(false)}
				disabled={disabled}
				style={styles.container}
				accessibilityRole="switch"
				accessibilityState={{ checked: selected, disabled }}
			>
				<Animated.View style={[styles.track, trackAnimatedStyle]}>
					<Animated.View
						style={[
							styles.knobBackground,
							knobBackgroundAnimatedStyle,
							{
								display: disabled ? "none" : "flex",
							},
						]}
					/>
					<Animated.View
						style={[
							styles.knob,
							knobAnimatedStyle,
							{
								backgroundColor: disabled
									? selected
										? theme.colors.surface
										: theme.colors.onSurface
									: selected
										? theme.colors.primaryContainer
										: theme.colors.outline,
							},
						]}
					>
						{icon && (
							<SwitchIcon
								selected={selected}
								color={
									selected ? theme.palette.text.primary : theme.colors.onPrimary
								}
							/>
						)}
					</Animated.View>
				</Animated.View>
			</Pressable>
		);
	},
);

const styles = StyleSheet.create({
	container: {
		alignItems: "center",
		justifyContent: "center",
	},
	track: {
		width: TRACK_WIDTH,
		height: TRACK_HEIGHT,
		borderRadius: TRACK_HEIGHT / 2,
		paddingHorizontal: 2,
		justifyContent: "center",
	},
	knob: {
		width: KNOB_SIZE,
		height: KNOB_SIZE,
		borderRadius: KNOB_SIZE / 2,
		alignItems: "center",
		justifyContent: "center",
	},
	knobBackground: {
		position: "absolute",
		width: KNOB_BACKGROUND_SIZE,
		height: KNOB_BACKGROUND_SIZE,
		borderRadius: KNOB_BACKGROUND_SIZE / 2,
		left: -6,
		top: (TRACK_HEIGHT - KNOB_BACKGROUND_SIZE - 4) / 2,
	},
});

export default Switch;
